import axios from 'axios'
import { useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { isEmpty, times } from 'remeda'
import { Grid, Mousewheel } from 'swiper'
import { SwiperSlide } from 'swiper/react'
import { useMediaQuery } from 'usehooks-ts'
import * as v from 'valibot'
import { fakePixelImage, instagramCardsAmount } from '@velasca/constants'
import type { InstagramMedia } from '@velasca/types'
import { HomePageLink } from '@velasca/ui/home-page-link'
import { MaxWidthContainer } from '@velasca/ui/max-width-container'
import { SansTitle } from '@velasca/ui/sans-title'
import { SerifTitle } from '@velasca/ui/serif-title'
import { handleError } from '@velasca/utils/handle-error'
import {
  Anchor,
  Image,
  Section,
  Slider,
  Title,
  Video,
} from './InstagramFeed.styles'
import type { InstagramFeedProps } from './InstagramFeed.types'

const InstagramMediaSchema = v.object({
  id: v.string(),
  caption: v.string(),
  permalink: v.string(),
  timestamp: v.string(),
  media_type: v.picklist(['CAROUSEL_ALBUM', 'IMAGE', 'VIDEO']),
  media_url: v.string(),
  username: v.string(),
})

const InstagramMediasSchema = v.array(InstagramMediaSchema)

export const InstagramFeed = ({
  upperTitle,
  title,
  link,
  instagramUrl,
}: InstagramFeedProps) => {
  const isMinWidth768px = useMediaQuery('(min-width: 768px)')
  const [isLoading, setIsLoading] = useState(true)
  const [instagramMedia, setInstagramMedia] = useState<InstagramMedia[] | []>(
    [],
  )

  useEffect(() => {
    axios
      .get(instagramUrl)
      .then(({ data }) => {
        const parsedInstagramResponse = v.safeParse(InstagramMediasSchema, data)

        if (parsedInstagramResponse.success) {
          setInstagramMedia(
            parsedInstagramResponse.output.slice(0, instagramCardsAmount),
          )
        } else {
          handleError(parsedInstagramResponse.issues, {
            message: 'Invalid data received from Instagram API.',
          })
        }
        setIsLoading(false)
      })
      .catch((error) =>
        handleError(error, { message: "Couldn't fetch the Instagram feed." }),
      )
  }, [instagramUrl])

  if (isEmpty(instagramMedia)) {
    return null
  }

  return (
    <Section>
      <Title>
        <SansTitle
          tracking="wide"
          as="h3"
          size={isMinWidth768px ? 'base' : 'sm'}
          color="primary"
        >
          {upperTitle}
        </SansTitle>
        <HomePageLink href={link}>
          <SerifTitle
            as="h2"
            size={isMinWidth768px ? 'xl' : 'md'}
            color="primary"
          >
            {title}
          </SerifTitle>
        </HomePageLink>
      </Title>
      <MaxWidthContainer>
        <Slider
          centeredSlides
          loop
          modules={[Grid, Mousewheel]}
          mousewheel={{ forceToAxis: true }}
          slidesPerView="auto"
          spaceBetween={10}
          breakpoints={{
            768: {
              enabled: false,
              grid: {
                rows: 2,
                fill: 'row',
              },
              slidesPerView: 6,
              loop: false,
              centeredSlides: false,
            },
          }}
        >
          {isLoading
            ? times(12, (index) => (
                <SwiperSlide key={index}>
                  <Skeleton height="100%" width="100%" />
                </SwiperSlide>
              ))
            : instagramMedia.map(
                ({ caption, permalink, media_type, media_url }) => {
                  return (
                    <SwiperSlide key={permalink}>
                      <Anchor target="_blank" href={permalink} rel="noreferrer">
                        {media_type === 'IMAGE' ||
                        media_type === 'CAROUSEL_ALBUM' ? (
                          <Image
                            className="lazyload"
                            src={fakePixelImage}
                            data-src={media_url}
                            alt={caption}
                          />
                        ) : (
                          <Video
                            muted
                            autoPlay
                            loop
                            playsInline
                            controls={false}
                            className="lazyload"
                            preload="none"
                            controlsList="nodownload"
                            data-src={media_url}
                            onContextMenu={(e) => {
                              // needed to prevent video download on right click.
                              e.preventDefault()

                              return false
                            }}
                          />
                        )}
                      </Anchor>
                    </SwiperSlide>
                  )
                },
              )}
        </Slider>
      </MaxWidthContainer>
    </Section>
  )
}
